/**
 * Définir une condition d'achat (registre B26, lot P1) : pour tout un fournisseur, une famille de son tarif ou UNE
 * référence, la remise (ou les remises en cascade : « 50+10 » = 55 %) OU le statut « prix net », le coefficient
 * qui reconstitue le prix de vente (prix de vente = prix d'achat ÷ coefficient) et la date d'effet.
 *
 * Une condition ne REMPLACE rien : elle s'ajoute à l'historique, datée, et celle d'avant reste lisible. Aucune
 * valeur n'est proposée d'avance : la marge est la décision de la direction, une case vide reste vide.
 */
import { useState } from 'react';

import {
  ApiError,
  api,
  type ConditionAchat,
  type CreationConditionAchat,
  type ReferenceDuTarif,
  type TypeConditionAchat,
} from '../api/client';
import {
  COEFFICIENT_MAX,
  COEFFICIENT_MIN,
  controlerSaisie,
  formaterCoefficient,
  formaterPourcentage,
  libelleRemises,
  lireCascade,
  lireDecimalFr,
  margeDuCoefficient,
  phraseReferenceChoisie,
} from '../domaine/conditions-achat';
import { aujourdhuiLocal } from '../domaine/gammes-versions';
import { ChampReference } from './ChampReference';
import { Champ, ErreurFormulaire, Panneau } from './Formulaire';

export interface CibleCondition {
  readonly portee: 'FOURNISSEUR' | 'FAMILLE' | 'REFERENCE';
  /** Famille du tarif (portée FAMILLE). */
  readonly famille: string | null;
  /** Référence déjà connue (portée REFERENCE) ; `null` = à saisir (nouvelle exception). */
  readonly reference: string | null;
}

/** Les remises d'une condition existante, ré-écrites comme on les tape : « 50+10 ». */
function texteRemises(c: ConditionAchat | null): string {
  return c && c.kind === 'DISCOUNT' ? c.remises.map((r) => r.replace('.', ',')).join('+') : '';
}

export function FormulaireCondition({
  partyId,
  partyName,
  cible,
  actuelle,
  onFermer,
  onEnregistree,
}: {
  partyId: string;
  partyName: string;
  cible: CibleCondition;
  /** La condition en vigueur de cette portée : sert à pré-remplir (modifier = en poser une nouvelle). */
  actuelle: ConditionAchat | null;
  onFermer: () => void;
  onEnregistree: (resultat: CreationConditionAchat) => void;
}) {
  const [kind, setKind] = useState<TypeConditionAchat>(actuelle?.kind ?? 'DISCOUNT');
  const [remises, setRemises] = useState(texteRemises(actuelle));
  const [coefficient, setCoefficient] = useState(actuelle ? formaterCoefficient(actuelle.coefficient) : '');
  const [validFrom, setValidFrom] = useState(aujourdhuiLocal());
  const [note, setNote] = useState('');
  const [reference, setReference] = useState(cible.reference ?? '');
  // La ligne du tarif choisie dans la liste : elle dit CE QUE la référence désigne, pour repérer une confusion
  // (10020 pour 10040) avant d'enregistrer plutôt qu'après.
  const [choisie, setChoisie] = useState<ReferenceDuTarif | null>(null);
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);
  const [enCours, setEnCours] = useState(false);

  const portee =
    cible.portee === 'FOURNISSEUR'
      ? `tout ${partyName}`
      : cible.portee === 'FAMILLE'
        ? `la famille « ${cible.famille ?? ''} » de ${partyName}`
        : `une référence de ${partyName}`;

  // Aide en direct : la remise pondérée et ce que laisse le coefficient (jamais un prix : aucun prix n'est saisi ici).
  const cascade = kind === 'DISCOUNT' && remises.trim() !== '' ? lireCascade(remises) : null;
  const lueCoefficient = coefficient.trim() !== '' ? lireDecimalFr(coefficient, 4) : null;
  const marge = lueCoefficient?.ok ? margeDuCoefficient(lueCoefficient.valeur) : null;
  const phraseChoisie = phraseReferenceChoisie(choisie, reference);

  const enregistrer = async () => {
    const controle = controlerSaisie({ kind, remises, coefficient });
    if (controle.erreurs.length > 0) {
      setErreur(new Error(controle.erreurs.join(' ')));
      return;
    }
    if (cible.portee === 'REFERENCE' && reference.trim() === '') {
      setErreur(new Error('Indique la référence de l’exception.'));
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(validFrom)) {
      setErreur(new Error('La date d’effet est obligatoire.'));
      return;
    }
    setEnCours(true);
    setErreur(null);
    try {
      const resultat = await api.definirConditionAchat({
        partyId,
        famille: cible.portee === 'FAMILLE' ? cible.famille : null,
        reference: cible.portee === 'REFERENCE' ? reference.trim() : null,
        kind,
        remises: controle.remisesLues ?? [],
        coefficient: controle.coefficientLu ?? '',
        validFrom,
        note: note.trim() === '' ? null : note.trim(),
      });
      onEnregistree(resultat);
    } catch (e) {
      setErreur(e instanceof ApiError || e instanceof Error ? e : new Error('Enregistrement impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Panneau
      titre={actuelle ? 'Nouvelle condition d’achat' : 'Définir une condition d’achat'}
      sousTitre={`S’applique à ${portee}. Elle s’ajoute à l’historique : les conditions précédentes restent lisibles et les gammes déjà publiées ne changent pas.`}
      onFermer={onFermer}
      actions={
        <>
          <button className="secondaire" onClick={onFermer} disabled={enCours}>
            Annuler
          </button>
          <button onClick={() => void enregistrer()} disabled={enCours}>
            {enCours ? 'Enregistrement…' : 'Enregistrer la condition'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void enregistrer();
        }}
      >
        <ErreurFormulaire
          message={erreur?.message ?? null}
          details={erreur instanceof ApiError ? erreur.details : undefined}
        />

        {cible.portee === 'REFERENCE' && (
          <Champ
            libelle="Référence"
            requis
            aide="Tape le début de la référence : la liste te montre celles du tarif, tu choisis. L’exception vaut pour cette seule référence, quelle que soit sa famille ; elle l’emporte sur la condition de sa famille."
          >
            {/* Règle de Benjamin : tout champ « référence » propose la liste par le DÉBUT de la référence. */}
            <ChampReference
              fournisseurId={partyId}
              libelle="Référence de l’exception"
              autoFocus={cible.reference === null}
              desactive={cible.reference !== null}
              placeholder="Début de la référence du fabricant"
              valeur={reference}
              onChange={setReference}
              onChoisir={(choix) => {
                setReference(choix.reference);
                setChoisie(choix);
              }}
            />
            {phraseChoisie && <p className="aide">{phraseChoisie}</p>}
          </Champ>
        )}

        <Champ libelle="Comment le fournisseur te facture" requis>
          <div className="options-impression">
            <label className="option-impression">
              <input type="radio" name="kind" checked={kind === 'DISCOUNT'} onChange={() => setKind('DISCOUNT')} />{' '}
              <strong>Remise sur le prix public</strong>
              <span className="aide">Le tarif donne le prix public ; tu bénéficies d’une ou plusieurs remises.</span>
            </label>
            <label className="option-impression">
              <input type="radio" name="kind" checked={kind === 'NET'} onChange={() => setKind('NET')} />{' '}
              <strong>Prix net</strong>
              <span className="aide">Le prix du tarif est déjà ton prix d’achat, sans remise.</span>
            </label>
          </div>
        </Champ>

        {kind === 'DISCOUNT' && (
          <Champ
            libelle="Remise(s)"
            requis
            aide="Un pourcentage. Plusieurs remises en cascade (au plus 3) : sépare-les par un + ; la première s’applique, puis la suivante sur le reste."
          >
            <input
              type="text"
              inputMode="decimal"
              autoFocus={cible.portee !== 'REFERENCE'}
              placeholder="Pourcentage(s), séparés par +"
              value={remises}
              onChange={(e) => setRemises(e.target.value)}
            />
            {cascade?.ok && cascade.remises.length > 1 && (
              <p className="aide">
                {libelleRemises(cascade.remises)} : c’est comme une remise unique de {formaterPourcentage(cascade.total)}.
              </p>
            )}
            {cascade && !cascade.ok && <p className="aide" style={{ color: 'var(--rouge)' }}>{cascade.erreur}</p>}
          </Champ>
        )}

        <Champ
          libelle="Coefficient"
          requis
          aide="Prix de vente = prix d’achat ÷ coefficient : plus il est petit, plus ta marge est grande. Aucun coefficient n’est jamais choisi à ta place."
        >
          <input
            type="text"
            inputMode="decimal"
            value={coefficient}
            onChange={(e) => setCoefficient(e.target.value)}
          />
          {lueCoefficient?.ok && marge !== null && <p className="aide">Marge : {marge} du prix de vente.</p>}
          {lueCoefficient?.ok && marge === null && Number(lueCoefficient.valeur) >= 1 && Number(lueCoefficient.valeur) <= COEFFICIENT_MAX && (
            <p className="aide" style={{ color: 'var(--orange)' }}>
              Un coefficient de 1 ou plus : le prix de vente ne dépasse pas le prix d’achat.
            </p>
          )}
          {lueCoefficient?.ok && (Number(lueCoefficient.valeur) < COEFFICIENT_MIN || Number(lueCoefficient.valeur) > COEFFICIENT_MAX) && (
            <p className="aide" style={{ color: 'var(--rouge)' }}>
              Le coefficient doit être compris entre {formaterCoefficient(String(COEFFICIENT_MIN))} et {COEFFICIENT_MAX}.
            </p>
          )}
          {lueCoefficient && !lueCoefficient.ok && (
            <p className="aide" style={{ color: 'var(--rouge)' }}>{lueCoefficient.erreur}</p>
          )}
        </Champ>

        <Champ
          libelle="Date d’effet"
          requis
          aide="La condition s’applique à partir de ce jour. Une date passée est acceptée ; une date future prépare le changement."
        >
          <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
        </Champ>

        <Champ libelle="Note" aide="Facultatif : d’où vient la condition.">
          <input type="text" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
        </Champ>
      </form>
    </Panneau>
  );
}
